import {
  Box,
  Button,
  Card,
  CardContent,
  Link,
  Stack,
  Typography,
} from '@mui/material'
import type { ErrorInfo, ReactNode } from 'react'
import { Component } from 'react'
import { Link as RouterLink, useLocation } from 'react-router-dom'
import { CopyableError } from '../testCrash/CopyableError'

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
  showDetails: boolean
}

interface ErrorBoundaryProps {
  children: ReactNode
  /** Key to reset the error boundary when location changes */
  resetKey?: string
}

/** Error fallback UI rendered by the boundary */
function ErrorFallback({
  error,
  showDetails,
  onToggleDetails,
  onReset,
}: {
  error: Error
  showDetails: boolean
  onToggleDetails: () => void
  onReset: () => void
}) {
  return (
    <Box sx={{ p: 3 }}>
      <Card>
        <CardContent>
          <Typography variant="h1" gutterBottom>
            Something went wrong
          </Typography>
          <Typography paragraph>
            We could not load this page content. Please try again.
          </Typography>

          <Link component={RouterLink} to="/search" onClick={onReset}>
            <Typography variant="h2">Return to Search</Typography>
          </Link>

          <Stack spacing={2} sx={{ mt: 3 }}>
            <Button variant="text" size="small" onClick={onToggleDetails}>
              {showDetails ? 'Hide' : 'Show'} Error Details
            </Button>
            {showDetails && <CopyableError error={error} />}
          </Stack>
        </CardContent>
      </Card>
    </Box>
  )
}

// Class component required by React for error boundary API
class RenderErrorCatcher extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null, showDetails: false }
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error }
  }

  static getDerivedStateFromProps(
    props: ErrorBoundaryProps,
    state: ErrorBoundaryState,
  ): Partial<ErrorBoundaryState> | null {
    // Reset error state when resetKey changes (location change)
    return null
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps) {
    // Reset error state when resetKey changes
    if (prevProps.resetKey !== this.props.resetKey && this.state.hasError) {
      this.setState({ hasError: false, error: null, showDetails: false })
    }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Render error in routed content:', error, info)
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, showDetails: false })
  }

  handleToggleDetails = () => {
    this.setState((prev) => ({ showDetails: !prev.showDetails }))
  }

  render() {
    if (this.state.hasError && this.state.error) {
      return (
        <ErrorFallback
          error={this.state.error}
          showDetails={this.state.showDetails}
          onToggleDetails={this.handleToggleDetails}
          onReset={this.handleReset}
        />
      )
    }

    return this.props.children
  }
}

export const PageErrorBoundary = ({ children }: { children: ReactNode }) => {
  const location = useLocation()
  // Use location as reset key to clear errors on navigation
  const resetKey = `${location.pathname}${location.search}${location.hash}`

  return <RenderErrorCatcher resetKey={resetKey}>{children}</RenderErrorCatcher>
}
